import { Auth } from "aws-amplify";
import { takeEvery, call, put, fork, select } from "redux-saga/effects";
import * as actions from "../../actions";
import {
  getDispatchFeedEvent,
  getDispatchFeedEventsPayload,
  getPinnedDispatchEventPayload,
  getPinnedDispatchEventsPayload,
  getTmcLogPayload,
  pinDispatchEventPayload,
  removePinDispatchEventPayload,
  types,
} from "../../actions";
import * as api from "../../api";
import { DispatchFeedStoreError, DispatchFeedStoreSuccess } from "../../enums/store-messages/dispatch";
import { IRootReducer } from "../../reducers";
import {
  DispatchEventApiResponse,
  DispatchEvent,
  FeedStatus,
  PagingKeys,
  PagingResponse,
  PinEvent,
  TmcLog,
} from "../../reducers/states/dispatch-event-feed-state";
import { IsResponseValidUtil } from "../../utils/api-utils";
import { sanitizeJogIds } from "../../reducers/states/agency/utils";
import { getAgencyId } from "../../reducers/states/agency";

const addAgencyId = (event: DispatchEventApiResponse): DispatchEvent=> {
  return {
    ...event,
    agencyId: getAgencyId(event.agency)
  }
}

export function* getSingleDispatchEvent(action: {
  type: typeof types.GET_DISPATCH_FEED_EVENT;
  payload: getDispatchFeedEvent;
}): Generator<any, void, unknown> {
  try {
    // Clear any previous data from appearing before the latest data loads
    yield put(actions.clearCurrentDispatchEvent());
    const result: any = yield call(api.getDispatchEvent, action.payload.id);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        const event = addAgencyId(result.data);
        
        yield put(actions.getDispatchFeedEventSuccess(event));
      }
    }
  } catch (e) {
    //TODO: Implement proper logging and error handling
    console.log(e);
  }
}
function* watchDispatchFeedEvent() {
  yield takeEvery(actions.types.GET_DISPATCH_FEED_EVENT, getSingleDispatchEvent);
}

export function* getDispatchFeedEvents(action: {
  type: typeof types.GET_DISPATCH_FEED_EVENTS;
  payload: getDispatchFeedEventsPayload;
}): Generator<any, void, unknown> {
  try {
    let pagingKeys = { pk1: "", sk1: "", pk2: "", sk2: "" } as PagingKeys;
    if (action.payload.pageResponse) {
      throw new Error("Paging not yet implemented.");
      let existingPagingKeys: any = yield select(
        (state: IRootReducer) => state.dispatchEventFeedReducer.dispatchEvents.pagingKeys,
      );
      pagingKeys = existingPagingKeys;
    }

    let jogIds = sanitizeJogIds(action.payload.jogIds);

    // Send params with quotes, to get around issue with API gateway mapping template changing "01" to "1". Related ticket: https://dev.azure.com/WSDOT/VCC/_workitems/edit/99066
    jogIds = jogIds.split(",").map(id => `"${id}"`).join(",");

    const result: any = yield call(
      api.getDispatchEvents,
      jogIds,
      action.payload.fromTimeStamp,
      action.payload.toTimeStamp,
      action.payload.showOpenDispatchesOnly,
      "", // Defaulting to not use page size
      pagingKeys,
    );

    if (result && result.status === 200 && IsResponseValidUtil(result.data)) {
      const apiResponse = result.data as PagingResponse<DispatchEventApiResponse[]>;

      // Use a Map to dedupe events based on ID. Duplicates are possible when they are associated with more than one jogId.
      const map = new Map<string, DispatchEvent>();

      // TODO: If this was a page request/response, add the existing data to the map from state
      // Paging is going to be broken intially by the backend's multiple queires by JogId.
      // Might need to store paging in state per jog, e.g., key/value pair, and expand on this original implementation.
      if (action.payload.pageResponse) {        
        let existingDispatchEvents = yield select(
          (state: IRootReducer) => state.dispatchEventFeedReducer.dispatchEvents.data,
        );
        
        (existingDispatchEvents as DispatchEvent[]).forEach((event) => {
          map.set(event.id || event.eventNumber, event);
        });
      }

      // Add the request results to the map
      apiResponse.data.forEach((event) => {
        if(event.id) {
          map.set(event.id, addAgencyId(event));
        }
      });

      yield put(actions.getDispatchFeedEventsSuccess({
        pagingKeys: apiResponse.pagingKeys,
        data: Array.from(map.values()),
      }));

      if (action.payload.setGeoData) {
        yield put(actions.setDispatchAPIGeoRequest([...map.values()]));
      }
    } else {
      yield put(actions.getDispatchFeedEventsFailure(DispatchFeedStoreError.DISPATCH_EVENT_FEED_GET));      
    }

  } catch (e) {
    yield put(actions.getDispatchFeedEventsFailure(DispatchFeedStoreError.DISPATCH_EVENT_FEED_GET));
  }
}
function* watchDispatchFeedEvents() {
  yield takeEvery(actions.types.GET_DISPATCH_FEED_EVENTS, getDispatchFeedEvents);
}

export function* getNoteworthyEvents(): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.getNoteworthyEvents);
    if (result && result.status === 200 && IsResponseValidUtil(result.data)) {
      // Review: Move this filter and a sort into the API Lambda
      const apiResultPayload = result.data as DispatchEventApiResponse[];      
      const allEvents = apiResultPayload.map(event => addAgencyId(event));
      allEvents.sort((d1, d2) => new Date(d2.lastUpdateDt).getTime() - new Date(d1.lastUpdateDt).getTime());
      
      const onlyOpen = yield select((state: IRootReducer) => state.userInfoReducer.userPreferences.showOpenDispatchesOnly);
      if (onlyOpen) {
        const openEvents = allEvents.filter((r) => r.status === "OPEN");
        yield put(actions.getNoteworthyEventsSuccess(openEvents));
      } else {
        yield put(actions.getNoteworthyEventsSuccess(allEvents));
      }
    } else {
      yield put(actions.getNoteworthyEventsFailure(DispatchFeedStoreError.NOTEWORTHY_EVENT_FEED_GET));
    }
  } catch (e) {
    yield put(actions.getNoteworthyEventsFailure(DispatchFeedStoreError.NOTEWORTHY_EVENT_FEED_GET));
  }
}
function* watchGetNoteworthyEvents() {
  yield takeEvery(actions.types.GET_NOTEWORTHY_DISPATCH_EVENTS, getNoteworthyEvents);
}

export function* getTmcLog(action: {
  type: typeof types.GET_TMC_LOG;
  payload: getTmcLogPayload;
}): Generator<any, void, unknown> {
  try {
    // Clear the current TMCLog loaded in
    yield put(actions.clearCurrentTmcLog());
    const result: any = yield call(api.getTmcLog, action.payload.wspId);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let apiResultPayload: TmcLog = result.data;
        yield put(actions.getTmcLogSuccess(apiResultPayload));
      }
    } else {
      yield put(actions.getTmcLogFailure(DispatchFeedStoreError.TMC_LOG_GET));
    }
  } catch (e) {
    yield put(actions.getTmcLogFailure(DispatchFeedStoreError.TMC_LOG_GET));
  }
}
function* watchGetTmcLog() {
  yield takeEvery(actions.types.GET_TMC_LOG, getTmcLog);
}

export function* getPinnedDispatchEvents(action: {
  type: typeof types.GET_PINNED_DISPATCH_EVENTS;
  payload: getPinnedDispatchEventsPayload;
}): Generator<any, void, unknown> {
  try {
    const session: any = yield Auth.currentSession();
    const email = session.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(
      api.getPinnedDispatchEvents,
      email,
      action.payload.fromTimeStamp,
      action.payload.toTimeStamp,
    );
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        const apiResponse = result.data as DispatchEventApiResponse[];
        const pinnedEvents: DispatchEvent[] = apiResponse.map(event => addAgencyId(event));

        yield put(actions.getPinnedDispatchEventsSuccess(pinnedEvents));
      } else {
        yield put(actions.getPinnedDispatchEventsFailure(DispatchFeedStoreError.DISPATCH_EVENTS_PIN_GET));
      }
    } else {
      yield put(actions.getPinnedDispatchEventsFailure(DispatchFeedStoreError.DISPATCH_EVENTS_PIN_GET));
    }
  } catch (e) {
    yield put(actions.getPinnedDispatchEventsFailure(DispatchFeedStoreError.DISPATCH_EVENTS_PIN_GET));
  }
}
function* watchGetPinnedDispatchEvents() {
  yield takeEvery(actions.types.GET_PINNED_DISPATCH_EVENTS, getPinnedDispatchEvents);
}

export function* getPinnedDispatchEvent(action: {
  type: typeof types.GET_PINNED_DISPATCH_EVENT;
  payload: getPinnedDispatchEventPayload;
}): Generator<any, void, unknown> {
  try {
    const session: any = yield Auth.currentSession();
    const email = session.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(api.getPinnedDispatchEvent, email, action.payload.dispatchId);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        const pinnedEvent: PinEvent = result.data;
        yield put(actions.getPinnedDispatchEventSuccess(pinnedEvent));
      } else {
        yield put(actions.getPinnedDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_GET));
      }
    } else {
      yield put(actions.getPinnedDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_GET));
    }
  } catch (e) {
    yield put(actions.getPinnedDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_GET));
  }
}
function* watchGetPinnedDispatchEvent() {
  yield takeEvery(actions.types.GET_PINNED_DISPATCH_EVENT, getPinnedDispatchEvent);
}

export function* pinDispatchEvents(action: {
  type: typeof types.PIN_DISPATCH_EVENT;
  payload: pinDispatchEventPayload;
}): Generator<any, void, unknown> {
  try {
    const session: any = yield Auth.currentSession();
    const email = session.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(api.setPinnedDispatchEvents, email, action.payload.eventsToPin);
    if (result && result.status === 200) {
      yield put(actions.pinDispatchEventSuccess(DispatchFeedStoreSuccess.DISPATCH_EVENT_PIN_SAVE));
    } else {
      yield put(actions.pinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
    }
  } catch (e) {
    yield put(actions.pinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
  }
}
function* watchPinDispatchEvents() {
  yield takeEvery(actions.types.PIN_DISPATCH_EVENT, pinDispatchEvents);
}

export function* removePinDispatchEvent(action: {
  type: typeof types.REMOVE_PIN_DISPATCH_EVENT;
  payload: removePinDispatchEventPayload;
}): Generator<any, void, unknown> {
  try {
    const session: any = yield Auth.currentSession();
    const email = session.getIdToken().payload["custom:vccEmail"];
    const result: any = yield call(api.removePinnedDispatchEvent, email, action.payload.dispatchId);
    if (result && result.status === 200) {
      yield put(actions.removePinDispatchEventSuccess(DispatchFeedStoreSuccess.DISPATCH_EVENT_PIN_REMOVE));
    } else {
      yield put(actions.removePinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
    }
  } catch (e) {
    yield put(actions.removePinDispatchEventFailure(DispatchFeedStoreError.DISPATCH_EVENT_PIN_SAVE));
  }
}
function* watchRemovePinDispatchEvent() {
  yield takeEvery(actions.types.REMOVE_PIN_DISPATCH_EVENT, removePinDispatchEvent);
}

interface FeedStatusApiResponse {
  agency: string;
  status: string;
}

export function* getDispatchFeedStatus(): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.getFeedStatus);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        // todo: jcavner: This endpoint should be changed to return agencyIds
        let statuses: FeedStatus[] = (result.data as FeedStatusApiResponse[])
        .map(s => {
          return {
            // Backwards compatibility for API returning Agency abbreviations instead of IDs
            agencyId: getAgencyId(s.agency),
            status: s.status,            
          }});

        yield put(actions.getFeedStatusSuccess(statuses));
      }
    }
  } catch (e) {
    //TODO: Implement proper logging and error handling
    console.log(e);
  }
}
function* watchDispatchFeedStatus() {
  yield takeEvery(actions.types.GET_DISPATCH_FEED_STATUS, getDispatchFeedStatus);
}

const dispatchEventFeedSagas = [
  fork(watchDispatchFeedEvent),
  fork(watchDispatchFeedEvents),
  fork(watchGetNoteworthyEvents),
  fork(watchGetTmcLog),
  fork(watchPinDispatchEvents),
  fork(watchRemovePinDispatchEvent),
  fork(watchGetPinnedDispatchEvent),
  fork(watchGetPinnedDispatchEvents),
  fork(watchDispatchFeedStatus),
];

export default dispatchEventFeedSagas;
